import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { LocationLayoutView } from "../../location/LocationLayout";
import { timelineCameraZoom } from "../../view/TimelineCameraZoom/zoom";
import { branchAt } from "../../rail/RailBranchMotion/motion";
import { allLocationRuns } from "../../location/LocationRun";
import type { LocationRun } from "../../location/LocationRun";
import type { InstantPartSnapshot } from "../BoardSample/instantSnapshot";
import type {
  CharacterMarkerGroup,
  CharacterMarkerOnBoard,
  LocationMarkerGroup,
  LocationPartMarker,
} from "../ChapterBoard/types";
import {
  BOARD_REFERENCE_STAGE_HEIGHT as STAGE_H,
  BOARD_REFERENCE_VIEWPORT_WIDTH as VIEW_W,
} from "../ChapterBoard/types";

export function locationRuns(layout: BookTimelineLayout): LocationRun[] {
  return allLocationRuns(layout);
}

export function referenceLayoutView(characterCount: number): LocationLayoutView {
  const homeZoom = timelineCameraZoom(VIEW_W, characterCount);
  return {
    zoom: homeZoom,
    stageW: VIEW_W,
    stageH: STAGE_H,
    characterCount,
  };
}

export function characterGroupIds(
  layout: BookTimelineLayout,
): Map<string, CharacterMarkerGroup> {
  const groups = new Map<string, CharacterMarkerGroup>();
  for (const stay of layout.rail.stays) {
    const branch = branchAt(layout.rail.branches, stay.rootPlaceId, stay.startMs);
    const branchId = branch?.id ?? stay.rootPlaceId;
    const placeId = stay.placeId ?? stay.rootPlaceId;
    const groupId = `${branchId}:${placeId}`;
    let group = groups.get(groupId);
    if (group === undefined) {
      group = { id: groupId, branchId, placeId, markerIds: [] };
      groups.set(groupId, group);
    }
    if (!group.markerIds.includes(stay.characterId)) {
      group.markerIds.push(stay.characterId);
    }
  }
  for (const group of groups.values()) {
    group.markerIds.sort((a, b) => a.localeCompare(b));
  }
  return groups;
}

export function emptyCharacterMarkers(
  layout: BookTimelineLayout,
  groups: Map<string, CharacterMarkerGroup>,
): CharacterMarkerOnBoard[] {
  const byCharacter = new Map<string, string>();
  for (const group of groups.values()) {
    for (const characterId of group.markerIds) {
      byCharacter.set(characterId, group.id);
    }
  }
  return layout.markers.map((marker) => ({
    id: marker.characterId,
    characterId: marker.characterId,
    groupId: byCharacter.get(marker.characterId) ?? marker.characterId,
    track: { keyframes: [] },
  }));
}

export function emptyLocationGroups(runs: LocationRun[]): LocationMarkerGroup[] {
  return runs.map((run) => ({
    id: run.runId,
    branchId: run.runId,
    site: {
      id: `${run.runId}:site`,
      siteLabel: run.locationLabel,
      track: { keyframes: [] },
    },
    parts: [],
  }));
}

export function ensurePartMarker(
  group: LocationMarkerGroup,
  part: InstantPartSnapshot,
  index: Map<string, LocationPartMarker>,
): LocationPartMarker {
  const key = `${group.branchId}:${part.label}`;
  const existing = index.get(key);
  if (existing !== undefined) {
    return existing;
  }
  const marker: LocationPartMarker = {
    id: key,
    placeId: part.placeId,
    partLabel: part.label,
    track: { keyframes: [] },
  };
  group.parts.push(marker);
  index.set(key, marker);
  return marker;
}
